/**
 * P0: Self-only profile photo recovery when Cloudinary URLs are missing/broken.
 * Peers never mutate; auth uid must match userId.
 */

import { supabase } from '@/integrations/supabase/client';
import { globalDataStore } from '@/lib/globalDataStore';
import { migrateLegacyProfilePhotoCache, setProfilePhotoCachePayload } from '@/lib/profilePhotoKeys';
import { setRemoteUrl as photoStoreSetRemoteUrl } from '@/lib/photoStore';
import { invalidateUserPhotoCache } from '@/lib/userPhotoSessionCache';
import { normalizeCloudinaryBaseUrl } from '@/utils/cloudinaryCanonical';
import { clearUserProfileCache, injectUserProfile } from '@/utils/userProfileCoordinator';
import { getDefaultGoogleImageUrl } from '@/lib/defaultProfilePhoto';
import { shouldUploadToCloudinary } from '@/lib/cloudinaryUploadGuard';
import { validateUpload, UploadBlockedError } from '@/lib/uploadGuard';

const LOG_PREFIX = '[SELF_PHOTO_RECOVERY]';

/** One-shot per (userId, failed normalized base) per page session */
const attempted = new Set<string>();

export type SelfPhotoRecoveryResult =
  | { ok: true; primaryUrl: string; source: 'reordered_photo_urls' | 'google_cloudinary_upload' }
  | { ok: false; reason: string };

function attemptKey(userId: string, failedSrc: string): string {
  return `${userId}|${normalizeForCompare(failedSrc)}`;
}

function normalizeForCompare(url: string): string {
  try {
    const u = url.split('?')[0];
    return normalizeCloudinaryBaseUrl(u);
  } catch {
    return url.split('?')[0];
  }
}

function urlsMatchAsset(a: string, b: string): boolean {
  return normalizeForCompare(a) === normalizeForCompare(b);
}

function parsePhotoUrls(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter((u): u is string => typeof u === 'string' && u.startsWith('http'));
  }
  if (typeof raw === 'string') {
    if (raw.startsWith('http')) return [raw];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((u): u is string => typeof u === 'string' && u.startsWith('http'));
      }
    } catch {
      /* ignore */
    }
  }
  return [];
}

function probeImageLoads(url: string, timeoutMs = 12000): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(t);
      resolve(ok);
    };
    const t = setTimeout(() => done(false), timeoutMs);
    img.onload = () => done(true);
    img.onerror = () => done(false);
    img.src = url;
  });
}

function mergePhotoContextL1Cache(userId: string, url: string): void {
  try {
    const raw = localStorage.getItem('blissnet_photo_cache_v145');
    const cache = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const key = String(userId);
    cache[key] = { url, timestamp: Date.now(), source: 'cloudinary' };
    localStorage.setItem('blissnet_photo_cache_v145', JSON.stringify(cache));
  } catch {
    /* ignore */
  }
}

/** Sync all caches + events after DB has the new photo_urls (or primary). */
async function syncSelfPhotoRecoveryCaches(
  userId: string,
  photoUrls: string[],
  primaryUrl: string
): Promise<void> {
  clearUserProfileCache(userId);
  invalidateUserPhotoCache(userId);

  const curr = globalDataStore.getProfile() ?? {};
  globalDataStore.updateProfile({
    ...curr,
    id: userId,
    photo_urls: photoUrls,
  });

  try {
    migrateLegacyProfilePhotoCache(userId);
    setProfilePhotoCachePayload(userId, {
      kind: 'url',
      url: primaryUrl,
      timestamp: Date.now(),
      ttl: 24 * 60 * 60 * 1000,
    });
  } catch {
    /* ignore */
  }

  try {
    await photoStoreSetRemoteUrl(userId, primaryUrl);
  } catch {
    /* ignore */
  }

  injectUserProfile(userId, { photo_urls: photoUrls });

  mergePhotoContextL1Cache(userId, primaryUrl);

  window.dispatchEvent(
    new CustomEvent('photoUpdated', {
      detail: { userId, photoUrl: primaryUrl },
    })
  );

  window.dispatchEvent(
    new CustomEvent('blissnet-self-photo-urls-updated', {
      detail: { userId, photo_urls: photoUrls },
    })
  );
}

/**
 * Attempt recovery for the current user only when an image load failed.
 * @param failedSrc - The img.src that failed (e.g. optimized Cloudinary URL)
 */
export async function attemptRecoverSelfProfilePhoto(
  userId: string,
  failedSrc: string
): Promise<SelfPhotoRecoveryResult> {
  if (!userId || !failedSrc || !failedSrc.startsWith('http')) {
    return { ok: false, reason: 'skip_non_http_or_empty' };
  }

  const { data: authData } = await supabase.auth.getUser();
  const authUser = authData?.user;
  if (!authUser?.id || authUser.id !== userId) {
    return { ok: false, reason: 'not_self' };
  }

  const key = attemptKey(userId, failedSrc);
  if (attempted.has(key)) {
    return { ok: false, reason: 'already_attempted' };
  }
  attempted.add(key);

  console.warn(`${LOG_PREFIX} start userId=${userId.slice(0, 8)}… failed=${failedSrc.slice(0, 72)}…`);

  const { data: row, error } = await supabase
    .from('users')
    .select('photo_urls')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.warn(`${LOG_PREFIX} load photo_urls failed`, error.message);
    return { ok: false, reason: 'db_load_error' };
  }

  const photoUrls = parsePhotoUrls(row?.photo_urls);
  if (photoUrls.length === 0) {
    return tryGoogleUploadRecover(userId, authUser);
  }

  let badIndex = photoUrls.findIndex((u) => urlsMatchAsset(u, failedSrc));
  if (badIndex < 0) {
    badIndex = 0;
  }

  const badUrl = photoUrls[badIndex];
  const withoutBad = photoUrls.filter((u) => !urlsMatchAsset(u, badUrl));

  for (const candidate of withoutBad) {
    const base = normalizeCloudinaryBaseUrl(candidate.split('?')[0]);
    const ok = await probeImageLoads(base);
    if (ok) {
      const rest = withoutBad.filter((u) => u !== candidate);
      const newUrls = [candidate, ...rest];

      const { error: upErr } = await supabase
        .from('users')
        .update({
          photo_urls: newUrls,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (upErr) {
        console.warn(`${LOG_PREFIX} reorder failed`, upErr.message);
        return tryGoogleUploadRecover(userId, authUser);
      }

      const primary = newUrls[0];
      await syncSelfPhotoRecoveryCaches(userId, newUrls, primary);
      console.warn(`${LOG_PREFIX} success source=reordered_photo_urls primary=${primary.slice(0, 64)}…`);
      return { ok: true, primaryUrl: primary, source: 'reordered_photo_urls' };
    }
  }

  return tryGoogleUploadRecover(userId, authUser);
}

async function tryGoogleUploadRecover(
  userId: string,
  authUser: any
): Promise<SelfPhotoRecoveryResult> {
  const googleUrl = getDefaultGoogleImageUrl(authUser);
  if (!googleUrl || !shouldUploadToCloudinary(googleUrl)) {
    console.warn(`${LOG_PREFIX} no Google avatar or upload not allowed`);
    return { ok: false, reason: 'no_google_or_guard' };
  }

  try {
    const res = await fetch(googleUrl);
    if (!res.ok) {
      console.warn(`${LOG_PREFIX} Google fetch failed`, res.status);
      return { ok: false, reason: 'google_fetch_failed' };
    }
    const blob = await res.blob();
    const ext = blob.type?.includes('png') ? 'png' : 'jpg';
    const file = new File([blob], `recover-${userId}.${ext}`, { type: blob.type || 'image/jpeg' });

    try {
      validateUpload(file, userId, 'selfProfilePhotoRecovery');
    } catch (e) {
      if (e instanceof UploadBlockedError) {
        console.warn(`${LOG_PREFIX} upload blocked by validateUpload`);
        return { ok: false, reason: 'upload_blocked' };
      }
      throw e;
    }

    let userName: string | undefined;
    const { data: nameRow } = await supabase.from('users').select('name').eq('id', userId).maybeSingle();
    userName = nameRow?.name || authUser?.user_metadata?.name || authUser?.user_metadata?.full_name;

    const { uploadProfilePhotoSigned, updateUserProfilePhoto } = await import('@/lib/cloudinarySignedUpload');
    const { secureUrl, publicId } = await uploadProfilePhotoSigned(file, userName);

    await updateUserProfilePhoto(userId, secureUrl, publicId);

    await syncSelfPhotoRecoveryCaches(userId, [secureUrl], secureUrl);
    console.warn(`${LOG_PREFIX} success source=google_cloudinary_upload primary=${secureUrl.slice(0, 64)}…`);
    return { ok: true, primaryUrl: secureUrl, source: 'google_cloudinary_upload' };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn(`${LOG_PREFIX} Google→Cloudinary path failed`, msg);
    return { ok: false, reason: msg };
  }
}
